import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, ZoomControl, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './App.css';
// Utilities
import { socket, api } from './utils/api';
import { getDistanceMeters, formatPin } from './utils/helpers';
// Components
import LoadingScreen from './components/LoadingScreen';
import Header from './components/Header';
import GlobalPulse from './components/GlobalPulse';
import SkyCast from './components/SkyCast';
import FloatingMenu from './components/FloatingMenu';
// Modals
import AboutModal from './components/modals/AboutModal';
import StatsModal from './components/modals/StatsModal'; 
import ThoughtModal from './components/modals/ThoughtModal';
import StoryModal from './components/modals/StoryModal';
import Notification from './components/modals/Notification';
import PrivacyModal from './components/modals/PrivacyModal';
// Map Components
import FireflyLayer from './components/map/FireflyLayer';
import MapHandler from './components/map/MapHandler';
import MapMarkers from './components/map/MapMarkers';

const BASE_RADIUS_METERS = 5000;
const MAX_CHARS = 500;

function App() {
  const [pins, setPins] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(6);
  
  const [showAbout, setShowAbout] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); 

  const [notification, setNotification] = useState(null);
  const [showInput, setShowInput] = useState(false);

  const [tempLocation, setTempLocation] = useState(null);
  const [thoughtText, setThoughtText] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);


  const [isMuted, setIsMuted] = useState(false);
  const [resonatedIds, setResonatedIds] = useState(new Set());
  
  const audioRefs = useRef({
    bgm: new Audio('/sounds/bgm.mp3'),
    positive: new Audio('/sounds/positive.mp3'),
    distressed: new Audio('/sounds/distressed.mp3'),
    healed: new Audio('/sounds/healed.mp3'),
    resonate: new Audio('/sounds/resonate.mp3')
  });

  // --- AUDIO LOGIC ---
  useEffect(() => {
    const bgm = audioRefs.current.bgm;
    bgm.loop = true; 
    bgm.volume = 0.2;
    // Auto-play might be blocked by browser, usually requires interaction
    return () => bgm.pause();
  }, []);

  // FIX 3: Unified Audio Handler (Deleted 'toggleSound')
  const toggleMute = () => {
    setIsMuted(prev => {
      const next = !prev;
      if (next) audioRefs.current.bgm.pause();
      else audioRefs.current.bgm.play().catch(() => {});
      return next;
    });
  };

  const playSound = (type) => {
    if (isMuted) return;
    const sound = audioRefs.current[type];
    if (sound) { sound.currentTime = 0; sound.volume = 0.5; sound.play().catch(() => {}); }
  };

  // --- LOCATION PERMISSION & FETCHING ---
  const requestLocationPermissionAndFetch = async () => {
    // 1. Try Native Capacitor Geolocation Permission request if on mobile
    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        const permStatus = await Geolocation.requestPermissions();
        if (permStatus.location === 'granted' || permStatus.coarseLocation === 'granted') {
          const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setUserLocation(loc);
          return loc;
        } else {
          setNotification({
            icon: "📍",
            title: "Location Access Required",
            message: "Location permission is required to post messages on the map. Please allow location access."
          });
          return null;
        }
      } catch (err) {
        console.warn("Capacitor Geolocation permission error:", err);
      }
    }

    // 2. Standard Web Browser Geolocation fallback
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setNotification({
          icon: "⚠️",
          title: "Unsupported Feature",
          message: "Geolocation is not supported by your device or browser."
        });
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setUserLocation(loc);
          resolve(loc);
        },
        (err) => {
          console.error("Browser location error:", err);
          setNotification({
            icon: "📍",
            title: "Location Access Required",
            message: "Location permission is required to post messages on the map. Please allow location access."
          });
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // --- DATA LOADING ---
  useEffect(() => {
    requestLocationPermissionAndFetch();

    const fetchData = async () => {
      try {
        const res = await api.get('/');
        setPins(res.data.map(formatPin));
        setTimeout(() => setIsLoading(false), 800);
      } catch (e) { setIsLoading(false); }
    };
    fetchData();

  socket.on('thought_created', (newT) => {
      const p = formatPin(newT);
      if (p) {
        setPins(prev => {
          // Check if we already added this pin locally to prevent duplicates
          if (prev.some(existingPin => existingPin.id === p.id)) return prev;
          return [p, ...prev];
        });
      }
    });

    socket.on('thought_updated', (updatedT) => {
      setPins(prev => prev.map(p => p.id === (updatedT._id || updatedT.id) ? formatPin(updatedT) : p));
    });

    return () => { socket.off('thought_created'); socket.off('thought_updated'); };
  }, []);

  // --- CALCULATIONS ---
  const { currentRadius, tierLevel } = useMemo(() => {
    if (!userLocation) return { currentRadius: BASE_RADIUS_METERS, tierLevel: 0 };
    
    // Logic: Count positive lights nearby to boost radius visually for the user
    const count = pins.filter(t => t.sentiment === 'positive' && getDistanceMeters(userLocation.lat, userLocation.lng, t.position.lat, t.position.lng) <= BASE_RADIUS_METERS).length;
    const tier = Math.floor(count / 10);
    const boost = tier * 0.10;
    
    return { 
      tierLevel: tier, 
      currentRadius: BASE_RADIUS_METERS * (1 + boost) 
    };
  }, [userLocation, pins]);

  // --- HANDLERS ---
  const handleResonate = async (pinId) => {
    if (resonatedIds.has(pinId)) return;
    setResonatedIds(prev => new Set(prev).add(pinId));
    playSound('resonate');
    
    setPins(prev => prev.map(p => p.id === pinId ? { ...p, resonanceCount: (p.resonanceCount || 0) + 1 } : p));
    
    try { await api.put(`/${pinId}/resonate`); } catch (e) { console.warn("Resonance sync failed"); }
  };

const handleSubmitThought = async (mlResult = {}) => {
    if (!thoughtText.trim()) return;
    setIsAnalyzing(true);
    
    // Extract local Google ML Kit classification result
    const localSentiment = mlResult.sentiment || 'positive';
    const localCategory = mlResult.category || 'General';
    const localLanguage = mlResult.language || 'English';
    const bornHealed = localSentiment === 'positive';

    let serverId = Date.now();
    let serverMessage = bornHealed 
      ? "Your positive signal shines across the night." 
      : "Your signal has been placed on the sanctuary map.";

    try {
      // Optional background sync if server is active
      const res = await api.post('/', { 
        text: thoughtText, 
        lat: tempLocation.lat, 
        lng: tempLocation.lng, 
        trap: honeypot,
        mlKitCategory: localCategory,
        detectedLanguage: localLanguage,
        sentiment: localSentiment
      });

      if (res && res.data && res.data._id) {
        serverId = res.data._id;
        if (res.data.message) serverMessage = res.data.message;
      }
    } catch (e) { 
      console.log("[Local ML Kit] Server unreachable, utilizing on-device ML Kit classification result.");
    }

    // Always create and place the pin using on-device Google ML Kit classification
    const newPin = {
      id: serverId,
      position: { lat: tempLocation.lat, lng: tempLocation.lng },
      text: thoughtText,
      sentiment: localSentiment,
      category: localCategory,
      language: localLanguage,
      isHealed: bornHealed,
      lightCount: 0,
      resonanceCount: 0
    };

    setPins(prevPins => [...prevPins, newPin]); 

    // Reset UI
    setIsModalOpen(false); 
    setThoughtText(""); 
    setHoneypot(""); 
    setTempLocation(null);
    
    // Play sound & notify
    if (bornHealed) { 
      playSound('positive'); 
      setNotification({ icon: "✨", title: "Beacon Lit", message: serverMessage }); 
    } else { 
      playSound('distressed'); 
      setNotification({ icon: "🌙", title: "Signal Received", message: serverMessage }); 
    }

    setIsAnalyzing(false);
  };

  // --- HANDLE PENCIL BUTTON CLICK ---
  const handleOpenInput = async () => {
    let loc = userLocation;

    // Prompt user for location permission if coordinates are not available yet
    if (!loc) {
      setNotification({ icon: "📡", title: "Locating...", message: "Location is required to post. Requesting permission..." });
      loc = await requestLocationPermissionAndFetch();
      if (!loc) return;
    }

    // --- PRIVACY FUZZING ALGORITHM ---
    const R = 0.009; // Max radius in degrees (approx 1km)
    const angle = Math.random() * 2 * Math.PI;
    const distance = Math.sqrt(Math.random()) * R; 

    const latOffset = distance * Math.cos(angle);
    const lngOffset = (distance * Math.sin(angle)) / Math.cos(loc.lat * (Math.PI / 180));

    const fuzzedLocation = {
      lat: loc.lat + latOffset,
      lng: loc.lng + lngOffset
    };

    setTempLocation(fuzzedLocation);
    setIsModalOpen(true);
  };

  const handleOpenPrivacy = () => {
    const isNativeMobile = window.Capacitor && window.Capacitor.isNativePlatform();
    const isMobileScreen = window.innerWidth <= 768;
    if (!isNativeMobile && !isMobileScreen) {
      window.open('/privacy.html', '_blank', 'noopener,noreferrer');
    } else {
      setShowPrivacy(true);
    }
  };

  return (
    <div className="app-container">
      {isLoading && <LoadingScreen />}
      
      {/* Header uses unified toggleMute */}
      <Header isMuted={isMuted} toggleMute={toggleMute} />
      
      <GlobalPulse socket={socket} />
      


      <MapContainer center={userLocation || [12.8797, 121.7740]} zoom={userLocation ? 13 : 6} minZoom={5} scrollWheelZoom={true} className="map-view" zoomControl={false}>
        <ZoomControl position="topright" />
        <TileLayer attribution='&copy; CARTO' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
        <FireflyLayer />
        
        {showInput && (
          <SkyCast 
             onClose={() => setShowInput(false)}
             onPostMessage={(data) => {
                socket.emit('add_thought', { ...data, timestamp: new Date() });
                // Note: SkyCast calls onClose() internally after animation
             }} 
          />
       )}

        <MapHandler 
          setZoomLevel={setZoomLevel} 
          setTempLocation={setTempLocation} 
          setIsModalOpen={setIsModalOpen} 
          userLocation={userLocation} 
          showNotification={(i,t,m) => setNotification({icon:i, title:t, message:m})} 
          radiusLimit={currentRadius} 
        />

        {userLocation && (
          <Circle 
            center={userLocation} 
            radius={currentRadius} 
            pathOptions={{ color: tierLevel >= 3 ? '#ffd700' : '#00ffff', fillColor: tierLevel >= 3 ? '#ffd700' : '#00ffff', fillOpacity: 0.08, weight: 1, dashArray: '10, 10' }} 
          />
        )}

        <MapMarkers 
          pins={pins} 
          zoomLevel={zoomLevel} 
          resonatedIds={resonatedIds} 
          handleResonate={handleResonate} 
        />
      </MapContainer>

       <FloatingMenu 
          onToggleSound={toggleMute}
          isMuted={isMuted}
          onOpenAbout={() => setShowAbout(true)}
          onOpenStats={() => setShowStats(true)}
          onOpenStory={() => setShowStory(true)}
          onOpenInput={handleOpenInput}
          pins={pins}             // The array of all thoughts
          userLocation={userLocation} // The user's current { lat, lng } state
       />

      
      <StatsModal 
        isOpen={showStats} 
        onClose={() => setShowStats(false)} 
        totalBeacons={pins.length} // Pass pins
        pins={pins}
      />

      
      <StoryModal 
        isOpen={showStory} 
        onClose={() => setShowStory(false)} 
      />

      <AboutModal 
        isOpen={showAbout} 
        onClose={() => setShowAbout(false)} 
        isMuted={isMuted} 
        onOpenPrivacy={handleOpenPrivacy}
      />

      <PrivacyModal
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />

      <ThoughtModal 
        isOpen={isModalOpen && !showAbout && !showStats} 
        text={thoughtText} setText={setThoughtText}
        honeypot={honeypot} setHoneypot={setHoneypot}
        onClose={() => { setIsModalOpen(false); setTempLocation(null); }}
        onSubmit={handleSubmitThought}
        isAnalyzing={isAnalyzing}
        maxLength={MAX_CHARS}
      />

      {notification && (
        <Notification 
          icon={notification.icon} 
          title={notification.title} 
          message={notification.message} 
          onClose={() => setNotification(null)} 
        />
      )}
    </div>
  );
}

export default App;